import {
  useGetTablesManagementConfigurationDownload,
  usePostTablesManagementConfiguration,
} from '@ipfabric/api-client/client/model/configuration/configuration';
import type {
  TableResponseBody,
  TableWithReportsRequestBody,
} from '@ipfabric/utils';
import { useQueryClient } from '@tanstack/react-query';
import { cachedQueryDefaultOptions } from 'utils/request/queryOptions';
import { useApi } from 'utils/request/useApi';
import { useTableQueryWithMandatoryColumns } from 'utils/table/useTableQueryWithMandatoryColumns';
import type {
  CompareManagementConfigurationConfig,
  ManagementConfigurationConfig,
} from './types';

export const useConfigurationDiffOptionsQuery = (
  firstConfig: CompareManagementConfigurationConfig,
  secondConfig: CompareManagementConfigurationConfig,
) => {
  const queryResult = usePostTablesManagementConfiguration(
    {
      columns: ['hash', 'lastChangeAt', 'hostname', 'sn'],
      filters: {
        or: [
          {
            sn: ['eq', firstConfig.sn],
          },
          {
            sn: ['eq', secondConfig.sn],
          },
        ],
      },
    },
    { query: cachedQueryDefaultOptions },
  );
  return useTableQueryWithMandatoryColumns(
    ['hash', 'lastChangeAt', 'hostname', 'sn'],
    queryResult,
  );
};

export const useConfigContentQuery = (sn: string, hash: string) =>
  useGetTablesManagementConfigurationDownload(
    { sn, hash },
    { query: cachedQueryDefaultOptions },
  );

export const useSingleConfigQuery = (): ((
  hash: string,
) => Promise<CompareManagementConfigurationConfig | undefined>) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return async (hash: string) => {
    const queryResult = await queryClient.fetchQuery(
      api.cachedQuery<
        TableResponseBody<CompareManagementConfigurationConfig>,
        TableWithReportsRequestBody<ManagementConfigurationConfig>
      >({
        method: 'POST',
        url: '/tables/management/configuration',
        data: {
          columns: ['hash', 'lastChangeAt', 'hostname', 'sn'],
          filters: {
            hash: ['eq', hash],
          },
          sort: {
            column: 'lastChangeAt',
            order: 'desc',
          },
          pagination: {
            start: 0,
            limit: 1,
          },
        },
      }),
    );

    return queryResult.data[0];
  };
};
